import { Banner, Button, IconButton } from '@ui';
import { useRef, useState } from 'react';
import type { RephraseResult, RephraseVariant } from './bugApi';
import { PANEL_TEXT_ATTR } from './capture';
import { AlertIcon, CheckIcon, CloseIcon, RefreshIcon, SparkIcon } from './icons';

/** What a variant says, for the next request as a rejected one. */
export const variantText = (variant: RephraseVariant): string =>
  variant.kind === 'question'
    ? variant.question
    : [variant.did && `Что делали: ${variant.did}`, variant.expected && `Что ждали: ${variant.expected}`, variant.guess && `Причина и решение: ${variant.guess}`]
        .filter(Boolean)
        .join('\n');

export const MAX_ROUND = 2;

type Phase = 'idle' | 'busy' | 'shown';

interface Options {
  /** Collects the cleaned package of the scenario and calls the service; throws with a readable message. */
  ask: (key: number, round: number, rejected: string[]) => Promise<RephraseResult>;
  /** The person picked a variant: only now the field changes (Рф5). */
  onPick: (key: number, variant: RephraseVariant) => void;
}

/** «Переформулировать» (Рф1–Рф6): the state of one request and its answer; the fields are touched only by a pick. */
export function useRephrase({ ask, onPick }: Options) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<RephraseResult | null>(null);
  const [error, setError] = useState('');
  /** The scenario the answer belongs to (Рф9): the one worked with last when the button was pressed. */
  const [key, setKey] = useState<number | null>(null);
  const round = useRef(0);
  const rejected = useRef<string[]>([]);
  const alive = useRef(0);

  const run = async (scenario: number, next: number) => {
    const call = (alive.current += 1);
    setPhase('busy');
    setError('');
    try {
      const got = await ask(scenario, next, rejected.current);
      if (call !== alive.current) return;
      round.current = next;
      setResult(got);
      setPhase('shown');
    } catch (err) {
      if (call !== alive.current) return;
      setError(err instanceof Error && err.message ? err.message : 'Не удалось переформулировать');
      setPhase(result ? 'shown' : 'idle');
    }
  };
  const start = (scenario: number) => {
    rejected.current = [];
    setResult(null);
    setKey(scenario);
    void run(scenario, 0);
  };
  const dislike = () => {
    if (!result || key === null) return;
    rejected.current = [...rejected.current, ...result.variants.map(variantText)].slice(-6);
    void run(key, round.current + 1);
  };
  const close = () => {
    alive.current += 1;
    setResult(null);
    setError('');
    setPhase('idle');
  };
  const pick = (variant: RephraseVariant) => {
    if (key !== null) onPick(key, variant);
    close();
  };
  return { phase, result, error, key, round: round.current, start, dislike, close, pick };
}

export type Rephrase = ReturnType<typeof useRephrase>;

/** Рф8: the button stands in the footer by the shot tools and «Отправить»; it asks about the scenario worked with last. */
export function RephraseButton({ rephrase, scenarioKey, disabled }: { rephrase: Rephrase; scenarioKey: number; disabled: boolean }) {
  const busy = rephrase.phase === 'busy';
  return (
    <Button size="sm" icon={<SparkIcon />} busy={busy} disabled={disabled || busy} onClick={() => rephrase.start(scenarioKey)}>
      Переформулировать
    </Button>
  );
}

/**
 * Рф9, Рф10: the answer in the body under the fields of its scenario. «Нравится» (tick) and «Не нравится» (again) share a row,
 * «Закрыть» is a lone cross in the corner of the card — no other button has one.
 */
export function RephraseAnswer({ rephrase, scenario, index }: { rephrase: Rephrase; scenario: number; index: number }) {
  const { phase, result, error } = rephrase;
  if (rephrase.key !== scenario || (!error && phase !== 'shown')) return null;
  const single = !!result && result.verdict === 'clear' && result.variants.length === 1;
  const spent = rephrase.round >= MAX_ROUND;
  const dislike = (
    <div className="bug-rp-bar">
      <Button size="sm" icon={<RefreshIcon />} disabled={spent} onClick={rephrase.dislike}>
        Не нравится
      </Button>
      {spent && <span className="muted small">Дальше — правьте текст сами</span>}
    </div>
  );
  return (
    <div className="bug-rp" aria-label={`Переформулировать, сценарий ${index + 1}`}>
      {error && (
        <Banner tone="error" icon={<AlertIcon />} className="bug-alert">
          {error} Поле не изменено.
        </Banner>
      )}
      {phase === 'shown' && result && (
        <div className="bug-rp-body">
          <IconButton label="Закрыть варианты" size="sm" icon={<CloseIcon />} className="bug-rp-close" onClick={rephrase.close} />
          {result.verdict === 'unclear' && (
            <Banner tone="warning" icon={<AlertIcon />} className="bug-rp-unclear">
              <strong>Понять нельзя.</strong> {result.why}
            </Banner>
          )}
          {result.variants.map((variant, i) => (
            <div key={i} className={`bug-rp-variant bug-rp-${variant.kind}`}>
              <div className="bug-rp-label">{variant.kind === 'question' ? `Вопрос: ${variant.label}` : variant.label || `Вариант ${i + 1}`}</div>
              <div className="bug-rp-text" {...{ [PANEL_TEXT_ATTR]: '' }}>
                {variant.kind === 'question' ? (
                  variant.question
                ) : (
                  <>
                    {variant.did && <div><b>Делали:</b> {variant.did}</div>}
                    {variant.expected && <div><b>Ждали:</b> {variant.expected}</div>}
                    {variant.guess && <div><b>Причина:</b> {variant.guess}</div>}
                  </>
                )}
              </div>
              {variant.kind === 'text' && (
                <div className="bug-rp-bar">
                  <Button size="sm" variant="primary" icon={<CheckIcon />} onClick={() => rephrase.pick(variant)}>
                    {single ? 'Нравится' : 'Подставить'}
                  </Button>
                  {single && (
                    <Button size="sm" icon={<RefreshIcon />} disabled={spent} onClick={rephrase.dislike}>
                      Не нравится
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
          {!single && dislike}
          {single && spent && <span className="muted small">Дальше — правьте текст сами</span>}
        </div>
      )}
    </div>
  );
}
